/**
 * PreAppLayout (TMMPDM-0075, TMM profile 1.1 TMM-B7): the shell of every screen outside `AppLayout`
 * — the sign-in landing, the signed-out and no-access pages, the router's error page, the
 * grant-required state and the boot gates. Every layout, including these, has a `<main>` landmark
 * and the skip link as its first focusable element; there is no navigation here, so the link only
 * moves focus into `<main>`. The screen supplies its own `h1`.
 */
import { useRef, type ReactNode } from 'react';
import { Box, Link } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';

export function PreAppLayout({ children }: { children: ReactNode }) {
  const { t } = useCopy();
  const mainRef = useRef<HTMLElement>(null);
  return (
    <>
      <Link
        href="#main"
        onClick={(event) => {
          event.preventDefault();
          mainRef.current?.focus();
        }}
        sx={{
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: (th) => th.zIndex.tooltip + 1,
          px: 2,
          py: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          transform: 'translateY(-200%)',
          '&:focus': { transform: 'none' },
        }}
      >
        {t('shell.skipToMain', 'Skip to main content')}
      </Link>
      <Box
        component="main"
        id="main"
        ref={mainRef}
        tabIndex={-1}
        sx={{ '&:focus': { outline: 'none' } }}
      >
        {children}
      </Box>
    </>
  );
}
